// ═══════════════════════════════════════════════════════════════════════════
// EXPENSE TAB - UrbanQ v1.9.6
// Tabla de gastos tipo Excel con CRUD, subcategorias y tipo Fijo/Variable
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw,
    FiTrendingDown, FiGrid, FiList, FiChevronUp, FiChevronDown,
    FiRepeat, FiCheck
} from 'react-icons/fi';
import FinancialModal from './FinancialModal';

export default function ExpenseTab({ year, month, token, apiUrl, scope, onNotification }) {
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [fixedTotal, setFixedTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterType, setFilterType] = useState('');
    const [gridLines, setGridLines] = useState('grid-lines');
    const [zebra, setZebra] = useState(false);
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // Inline editing
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');

    // Cargar categorias de gastos
    const fetchCategories = useCallback(async () => {
        try {
            const response = await fetch(`${apiUrl}/api/financial/categories?type=expense&scope=${scope}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    }, [apiUrl, token, scope]);

    // Cargar gastos
    const fetchExpenses = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${apiUrl}/api/financial/expense?year=${year}&month=${month}&scope=${scope}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setExpenses(result.data || []);
                setTotalAmount(result.totalAmount || 0);
                setFixedTotal(result.fixedTotal || 0);
            }
        } catch (err) {
            console.error('Error fetching expenses:', err);
            onNotification?.('Error al cargar gastos', 'error');
        } finally {
            setLoading(false);
        }
    }, [year, month, token, apiUrl, scope, onNotification]);

    useEffect(() => {
        fetchCategories();
        fetchExpenses();
    }, [fetchCategories, fetchExpenses]);

    // Cargar preferencias
    useEffect(() => {
        const savedGridLines = localStorage.getItem('finanzas_gridLines');
        const savedZebra = localStorage.getItem('finanzas_zebra');
        if (savedGridLines) setGridLines(savedGridLines);
        if (savedZebra) setZebra(savedZebra === 'true');
    }, []);

    const toggleGridLines = () => {
        const options = ['grid-lines', 'no-grid-lines', 'horizontal-lines'];
        const currentIndex = options.indexOf(gridLines);
        const newValue = options[(currentIndex + 1) % options.length];
        setGridLines(newValue);
        localStorage.setItem('finanzas_gridLines', newValue);
    };

    const toggleZebra = () => {
        const newValue = !zebra;
        setZebra(newValue);
        localStorage.setItem('finanzas_zebra', String(newValue));
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Filtrar y ordenar
    const filteredExpenses = expenses
        .filter(expense => {
            const matchSearch = expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                expense.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchCategory = !filterCategory || expense.category_id === parseInt(filterCategory);
            const matchType = !filterType || expense.expense_type === filterType;
            return matchSearch && matchCategory && matchType;
        })
        .sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            if (sortField === 'amount') {
                aVal = parseFloat(aVal);
                bVal = parseFloat(bVal);
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-EC', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // CRUD
    const handleSave = async (data) => {
        try {
            const url = editingExpense
                ? `${apiUrl}/api/financial/expense/${editingExpense.id}`
                : `${apiUrl}/api/financial/expense`;

            const method = editingExpense ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...data, scope })
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.(editingExpense ? 'Gasto actualizado' : 'Gasto creado', 'success');
                fetchExpenses();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error saving expense:', err);
            onNotification?.('Error al guardar', 'error');
            throw err;
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Estas seguro de eliminar este gasto?')) return;

        try {
            const response = await fetch(`${apiUrl}/api/financial/expense/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                onNotification?.('Gasto eliminado', 'success');
                fetchExpenses();
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            console.error('Error deleting expense:', err);
            onNotification?.('Error al eliminar', 'error');
        }
    };

    // Inline editing
    const handleCellDoubleClick = (expense, field) => {
        setEditingCell({ id: expense.id, field });
        setEditValue(expense[field] || '');
    };

    const handleCellBlur = async () => {
        if (!editingCell) return;

        const expense = expenses.find(e => e.id === editingCell.id);
        if (!expense || expense[editingCell.field] === editValue) {
            setEditingCell(null);
            return;
        }

        try {
            await handleSave({
                ...expense,
                [editingCell.field]: editingCell.field === 'amount' ? parseFloat(editValue) : editValue
            });
        } catch (err) {
            // Error ya manejado
        }
        setEditingCell(null);
    };

    const handleCellKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCellBlur();
        } else if (e.key === 'Escape') {
            setEditingCell(null);
        }
    };

    const openEditModal = (expense) => {
        setEditingExpense(expense);
        setModalOpen(true);
    };

    const openNewModal = () => {
        setEditingExpense(null);
        setModalOpen(true);
    };

    if (loading) {
        return (
            <div className="urbanq-finanzas-loading">
                <div className="urbanq-finanzas-spinner"></div>
            </div>
        );
    }

    const gridLinesLabel = {
        'grid-lines': 'Con lineas',
        'no-grid-lines': 'Sin lineas',
        'horizontal-lines': 'Solo horizontal'
    };

    return (
        <div className="urbanq-finanzas-expense-tab">
            <div className="urbanq-finanzas-section">
                <div className="urbanq-finanzas-section-header">
                    <h2><FiTrendingDown /> Gastos</h2>
                    <button className="urbanq-finanzas-btn primary" onClick={openNewModal}>
                        <FiPlus /> Nuevo Gasto
                    </button>
                </div>

                {/* Toolbar */}
                <div className="urbanq-finanzas-toolbar">
                    <div className="urbanq-finanzas-toolbar-left">
                        <div className="urbanq-finanzas-search-box">
                            <FiSearch />
                            <input
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="urbanq-finanzas-filter-group">
                            <span className="urbanq-finanzas-filter-label">Categoria:</span>
                            <select
                                className="urbanq-finanzas-select"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="">Todas</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="urbanq-finanzas-filter-group">
                            <span className="urbanq-finanzas-filter-label">Tipo:</span>
                            <select
                                className="urbanq-finanzas-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="Fijo">Fijo</option>
                                <option value="Variable">Variable</option>
                            </select>
                        </div>
                    </div>

                    <div className="urbanq-finanzas-toolbar-right">
                        <button
                            className={`urbanq-finanzas-grid-toggle ${gridLines === 'grid-lines' ? 'active' : ''}`}
                            onClick={toggleGridLines}
                        >
                            <FiGrid /> {gridLinesLabel[gridLines]}
                        </button>

                        <button
                            className={`urbanq-finanzas-grid-toggle ${zebra ? 'active' : ''}`}
                            onClick={toggleZebra}
                        >
                            <FiList /> Zebra
                        </button>

                        <button className="urbanq-finanzas-btn-icon" onClick={fetchExpenses}>
                            <FiRefreshCw />
                        </button>
                    </div>
                </div>

                {/* Barra de totales */}
                <div className="urbanq-finanzas-totals-bar">
                    <div className="urbanq-finanzas-totals-item">
                        <span className="urbanq-finanzas-totals-label">Total Gastos:</span>
                        <span className="urbanq-finanzas-totals-value negative">
                            {formatCurrency(totalAmount)}
                        </span>
                    </div>
                    <div className="urbanq-finanzas-totals-item">
                        <span className="urbanq-finanzas-totals-label">Gastos Fijos:</span>
                        <span className="urbanq-finanzas-totals-value">
                            {formatCurrency(fixedTotal)}
                        </span>
                    </div>
                    <div className="urbanq-finanzas-totals-item">
                        <span className="urbanq-finanzas-totals-label">Registros:</span>
                        <span className="urbanq-finanzas-totals-value">
                            {filteredExpenses.length}
                        </span>
                    </div>
                </div>

                {/* Tabla */}
                <div className="urbanq-finanzas-table-wrapper">
                    {filteredExpenses.length > 0 ? (
                        <table className={`urbanq-finanzas-table ${gridLines} ${zebra ? 'zebra' : ''}`}>
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort('date')}>
                                        Fecha
                                        {sortField === 'date' && (
                                            <span className="sort-icon">
                                                {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                        )}
                                    </th>
                                    <th className="sortable" onClick={() => handleSort('description')}>
                                        Descripcion
                                        {sortField === 'description' && (
                                            <span className="sort-icon">
                                                {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                        )}
                                    </th>
                                    <th>Categoria</th>
                                    <th>Subcategoria</th>
                                    <th className="sortable numeric" onClick={() => handleSort('amount')}>
                                        Monto
                                        {sortField === 'amount' && (
                                            <span className="sort-icon">
                                                {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                        )}
                                    </th>
                                    <th>Tipo</th>
                                    <th style={{ width: '60px', textAlign: 'center' }}>Rec.</th>
                                    <th>Metodo</th>
                                    <th style={{ width: '80px' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExpenses.map(expense => (
                                    <tr key={expense.id}>
                                        <td className="date">{formatDate(expense.date)}</td>

                                        <td
                                            className={editingCell?.id === expense.id && editingCell?.field === 'description' ? 'editing' : ''}
                                            onDoubleClick={() => handleCellDoubleClick(expense, 'description')}
                                        >
                                            {editingCell?.id === expense.id && editingCell?.field === 'description' ? (
                                                <input
                                                    type="text"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onBlur={handleCellBlur}
                                                    onKeyDown={handleCellKeyDown}
                                                    autoFocus
                                                />
                                            ) : (
                                                expense.description
                                            )}
                                        </td>

                                        <td>
                                            <span
                                                className="urbanq-finanzas-category-badge"
                                                style={{
                                                    background: `${expense.category_color}20`,
                                                    color: expense.category_color
                                                }}
                                            >
                                                {expense.category_name}
                                            </span>
                                        </td>

                                        <td style={{ color: '#718096', fontSize: '13px' }}>
                                            {expense.subcategory_name || '-'}
                                        </td>

                                        <td
                                            className={`numeric amount negative ${editingCell?.id === expense.id && editingCell?.field === 'amount' ? 'editing' : ''}`}
                                            onDoubleClick={() => handleCellDoubleClick(expense, 'amount')}
                                        >
                                            {editingCell?.id === expense.id && editingCell?.field === 'amount' ? (
                                                <input
                                                    type="number"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onBlur={handleCellBlur}
                                                    onKeyDown={handleCellKeyDown}
                                                    autoFocus
                                                    step="0.01"
                                                />
                                            ) : (
                                                formatCurrency(expense.amount)
                                            )}
                                        </td>

                                        <td>
                                            <span className={`urbanq-finanzas-type-badge ${expense.expense_type?.toLowerCase()}`}>
                                                {expense.expense_type}
                                            </span>
                                        </td>

                                        <td className="urbanq-finanzas-recurring">
                                            {expense.is_recurring ? (
                                                <FiRepeat title="Gasto recurrente" />
                                            ) : (
                                                <span style={{ color: '#e2e8f0' }}>-</span>
                                            )}
                                        </td>

                                        <td>{expense.payment_method}</td>

                                        <td>
                                            <div className="urbanq-finanzas-row-actions">
                                                <button
                                                    className="urbanq-finanzas-row-action"
                                                    onClick={() => openEditModal(expense)}
                                                    title="Editar"
                                                >
                                                    <FiEdit2 />
                                                </button>
                                                <button
                                                    className="urbanq-finanzas-row-action delete"
                                                    onClick={() => handleDelete(expense.id)}
                                                    title="Eliminar"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="urbanq-finanzas-empty">
                            <FiTrendingDown className="urbanq-finanzas-empty-icon" />
                            <h3>No hay gastos registrados</h3>
                            <p>Comienza agregando tu primer gasto</p>
                            <button className="urbanq-finanzas-btn primary" onClick={openNewModal}>
                                <FiPlus /> Agregar Gasto
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            <FinancialModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditingExpense(null);
                }}
                onSave={handleSave}
                type="expense"
                editData={editingExpense}
                categories={categories}
            />
        </div>
    );
}
